import {
  useState, useMemo 
} from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import {
  ChefHat, RefreshCw, FileText, Clock, CheckCircle, Calendar, Play 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  FormModal, FormField, FormInput, FormSelect, FormTextarea 
} from '@/components/ui/form-modal';
import {
  usePrepLists, useBatchPlans, useCreateBatchPlan, PrepListCard, PrepListDetailModal 
} from '@/features/production';
import type {
  PrepListStatus, PrepList, BatchPlanStatus 
} from '@/features/production';
import { toast } from 'sonner';
import { BatchPlanCard } from './batch-plan-card';

const DEFAULT_SITE_ID = 'site-celebration';

const TABS = [
  {
    id: 'prep',
    label: 'Prep Lists',
    href: '/production/prep',
    icon: FileText 
  },
  {
    id: 'batch',
    label: 'Batch Planning',
    href: '/production/batch',
    icon: Calendar 
  },
];

const STATUS_TABS: {
  value: PrepListStatus | undefined;
  label: string 
}[] = [
  {
    value: undefined,
    label: 'All' 
  },
  {
    value: 'draft',
    label: 'Draft' 
  },
  {
    value: 'active',
    label: 'Active' 
  },
  {
    value: 'completed',
    label: 'Completed' 
  },
];

const BATCH_STATUS_TABS: {
  value: BatchPlanStatus | undefined;
  label: string 
}[] = [
  {
    value: undefined,
    label: 'All' 
  },
  {
    value: 'draft',
    label: 'Draft' 
  },
  {
    value: 'scheduled',
    label: 'Scheduled' 
  },
  {
    value: 'in-progress',
    label: 'In Progress' 
  },
  {
    value: 'completed',
    label: 'Completed' 
  },
];

export function ProductionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<PrepListStatus | undefined>(undefined);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPrepList, setSelectedPrepList] = useState<PrepList | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    venueId: '',
    prepDate: '',
    notes: '' 
  });
  const [selectedBatchStatus, setSelectedBatchStatus] = useState<BatchPlanStatus | undefined>(undefined);
  const [selectedBatchPlans, setSelectedBatchPlans] = useState<string[]>([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchFormData, setBatchFormData] = useState({
    name: '',
    venueId: '',
    date: '',
    startTime: '06:00',
    endTime: '14:00',
    notes: '',
  });

  const activeTab = useMemo(() => (location.pathname.includes('/batch') ? 'batch' : 'prep'), [location.pathname]);
  const {
    data, isLoading, error, refetch, isFetching 
  } = usePrepLists(DEFAULT_SITE_ID, { status: selectedStatus });
  const { data: allData } = usePrepLists(DEFAULT_SITE_ID);
  const prepLists = data?.data ?? [];
  const {
    data: batchData, isLoading: batchLoading, error: batchError, refetch: refetchBatch 
  } = useBatchPlans(DEFAULT_SITE_ID, { status: selectedBatchStatus });
  const { data: allBatchData } = useBatchPlans(DEFAULT_SITE_ID);
  const batchPlans = batchData?.data ?? [];
  const createBatchMutation = useCreateBatchPlan(DEFAULT_SITE_ID);

  const stats = useMemo(() => {
    const lists = allData?.data ?? [];
    const totalItems = lists.reduce((sum, l) => sum + l.totalItems, 0);
    const completedItems = lists.reduce((sum, l) => sum + l.completedItems, 0);
    return {
      total: lists.length,
      draft: lists.filter((l) => l.status === 'draft').length,
      active: lists.filter((l) => l.status === 'active').length,
      completed: lists.filter((l) => l.status === 'completed').length,
      progress: totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0,
    };
  }, [allData]);

  const batchStats = useMemo(() => {
    const plans = allBatchData?.data ?? [];
    return {
      total: plans.length,
      draft: plans.filter((p) => p.status === 'draft').length,
      scheduled: plans.filter((p) => p.status === 'scheduled').length,
      inProgress: plans.filter((p) => p.status === 'in-progress').length,
      completed: plans.filter((p) => p.status === 'completed').length,
    };
  }, [allBatchData]);

  const handleAddPrepList = () => { setFormData({
    name: '',
    venueId: '',
    prepDate: '',
    notes: '' 
  }); setShowAddModal(true); };
  const handleSavePrepList = () => {
    if (!formData.name || !formData.venueId || !formData.prepDate) { toast.error('Please fill in all required fields'); return; }
    toast.success(`Created ${formData.name}`); setShowAddModal(false); refetch();
  };
  const handleBulkUpload = (file: File) => { toast.success(`Processing ${file.name}...`); setTimeout(() => { toast.success('Imported 5 prep lists'); refetch(); }, 1500); };
  const handlePrepListClick = (prepList: PrepList) => { setSelectedPrepList(prepList); setShowDetailModal(true); };
  const handleCloseDetailModal = () => { setShowDetailModal(false); setSelectedPrepList(null); };
  const handleActivatePrepList = (prepListId: string) => { toast.success(`Activated prep list ${prepListId}`); handleCloseDetailModal(); refetch(); };
  const handleCompletePrepList = (prepListId: string) => { toast.success(`Completed prep list ${prepListId}`); handleCloseDetailModal(); refetch(); };
  const handleStartItem = (_prepListId: string, itemId: string) => { toast.success(`Started item ${itemId}`); refetch(); };
  const handleCompleteItem = (_prepListId: string, itemId: string) => { toast.success(`Completed item ${itemId}`); refetch(); };
  const handleCreateBatchPlan = () => { setBatchFormData({
    name: '',
    venueId: '',
    date: '',
    startTime: '06:00',
    endTime: '14:00',
    notes: '' 
  }); setShowBatchModal(true); };
  const handleSaveBatchPlan = () => {
    if (!batchFormData.name || !batchFormData.venueId || !batchFormData.date) { toast.error('Please fill in all required fields'); return; }
    createBatchMutation.mutate({
      name: batchFormData.name,
      venueId: batchFormData.venueId,
      date: batchFormData.date,
      startTime: batchFormData.startTime,
      endTime: batchFormData.endTime,
      notes: batchFormData.notes || undefined 
    },
    {
      onSuccess: () => { toast.success(`Created batch plan: ${batchFormData.name}`); setShowBatchModal(false); refetchBatch(); },
      onError: () => { toast.error('Failed to create batch plan'); } 
    });
  };
  const handleDeleteBatchPlans = () => { toast.success(`Deleted ${selectedBatchPlans.length} batch plan(s)`); setSelectedBatchPlans([]); };
  const handleBatchBulkUpload = (file: File) => { toast.success(`Processing ${file.name}...`); setTimeout(() => { toast.success('Imported batch plans'); refetchBatch(); }, 1500); };
  const toggleBatchPlanSelection = (batchPlanId: string) => { setSelectedBatchPlans((prev) => prev.includes(batchPlanId) ? prev.filter((id) => id !== batchPlanId) : [...prev, batchPlanId]); };
  const exportData = prepLists.map((p) => ({
    prepListId: p.prepListId,
    venue: p.venueName,
    date: p.date,
    status: p.status,
    totalItems: p.totalItems,
    completedItems: p.completedItems 
  }));
  const batchExportData = batchPlans.map((b) => ({
    batchPlanId: b.batchPlanId,
    name: b.name,
    venue: b.venueName,
    date: b.date,
    startTime: b.startTime,
    endTime: b.endTime,
    status: b.status,
    totalItems: b.totalItems,
    completedItems: b.completedItems 
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-semibold">Production</h1><p className="text-muted-foreground">Manage prep lists and production schedules</p></div>
        <Button variant="outline" size="sm" onClick={() => activeTab === 'batch' ? refetchBatch() : refetch()} disabled={isFetching}><RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh</Button>
      </div>
      <div className="border-b"><nav className="flex gap-4">{TABS.map((tab) => { const Icon = tab.icon; const isActive = activeTab === tab.id; return (<button key={tab.id} type="button" onClick={() => navigate(tab.href)} className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'}`}><Icon className="h-4 w-4" />{tab.label}</button>); })}</nav></div>
      {activeTab === 'batch' ? (
        <>
          <CrudToolbar entityName="Batch Plan" entityNamePlural="Batch Plans" selectedCount={selectedBatchPlans.length} onAdd={handleCreateBatchPlan} onDelete={handleDeleteBatchPlans} onBulkUpload={handleBatchBulkUpload} exportData={batchExportData} exportFilename="batch-plans.csv" canEdit={false} />
          <FormModal title="Create Batch Plan" description="Schedule a new production batch" isOpen={showBatchModal} onClose={() => setShowBatchModal(false)} onSave={handleSaveBatchPlan} saveLabel="Create">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Name" required><FormInput value={batchFormData.name} onChange={(e) => setBatchFormData({
                ...batchFormData,
                name: e.target.value 
              })} placeholder="e.g., Morning Batch - Pastry" /></FormField>
              <FormField label="Venue" required><FormSelect value={batchFormData.venueId} onChange={(e) => setBatchFormData({
                ...batchFormData,
                venueId: e.target.value 
              })} placeholder="Select venue..." options={[{
                value: 'venue-main',
                label: 'Main Kitchen' 
              }, {
                value: 'venue-pastry',
                label: 'Pastry Kitchen' 
              }, {
                value: 'venue-cold',
                label: 'Cold Kitchen' 
              }]} /></FormField>
              <FormField label="Date" required><FormInput type="date" value={batchFormData.date} onChange={(e) => setBatchFormData({
                ...batchFormData,
                date: e.target.value 
              })} /></FormField>
              <div className="grid grid-cols-2 gap-2"><FormField label="Start Time" required><FormInput type="time" value={batchFormData.startTime} onChange={(e) => setBatchFormData({
                ...batchFormData,
                startTime: e.target.value 
              })} /></FormField><FormField label="End Time" required><FormInput type="time" value={batchFormData.endTime} onChange={(e) => setBatchFormData({
                ...batchFormData,
                endTime: e.target.value 
              })} /></FormField></div>
            </div>
            <FormField label="Notes"><FormTextarea value={batchFormData.notes} onChange={(e) => setBatchFormData({
              ...batchFormData,
              notes: e.target.value 
            })} placeholder="Special instructions or notes..." rows={2} /></FormField>
          </FormModal>
          <div className="grid gap-4 md:grid-cols-5">
            <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Plans</CardTitle><Calendar className="h-5 w-5 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{batchStats.total}</div></CardContent></Card>
            <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Draft</CardTitle><FileText className="h-5 w-5 text-gray-500" /></CardHeader><CardContent><div className="text-2xl font-bold text-gray-600">{batchStats.draft}</div></CardContent></Card>
            <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Scheduled</CardTitle><Clock className="h-5 w-5 text-blue-500" /></CardHeader><CardContent><div className="text-2xl font-bold text-blue-600">{batchStats.scheduled}</div></CardContent></Card>
            <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">In Progress</CardTitle><Play className="h-5 w-5 text-amber-500" /></CardHeader><CardContent><div className="text-2xl font-bold text-amber-600">{batchStats.inProgress}</div></CardContent></Card>
            <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle><CheckCircle className="h-5 w-5 text-green-500" /></CardHeader><CardContent><div className="text-2xl font-bold text-green-600">{batchStats.completed}</div></CardContent></Card>
          </div>
          <div className="flex flex-wrap gap-2">{BATCH_STATUS_TABS.map((tab) => (<Button key={tab.label} variant={selectedBatchStatus === tab.value ? 'default' : 'outline'} size="sm" onClick={() => setSelectedBatchStatus(tab.value)}>{tab.label}</Button>))}</div>
          {batchLoading && (<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => (<div key={`batch-skel-${i.toString()}`} className="h-48 animate-pulse rounded-lg bg-muted" />))}</div>)}
          {!batchLoading && batchError && (<Card><CardContent className="flex flex-col items-center justify-center py-12"><Calendar className="mb-4 h-12 w-12 text-red-500" /><p className="text-lg font-medium">Failed to load batch plans</p><Button variant="outline" className="mt-4" onClick={() => refetchBatch()}>Try Again</Button></CardContent></Card>)}
          {!batchLoading && !batchError && batchPlans.length === 0 && (<Card><CardContent className="flex flex-col items-center justify-center py-12"><Calendar className="mb-4 h-12 w-12 text-muted-foreground" /><p className="text-lg font-medium">Batch Planning</p><p className="text-sm text-muted-foreground">Schedule and optimize production batches</p><Button className="mt-4" onClick={handleCreateBatchPlan}>Create Batch Plan</Button></CardContent></Card>)}
          {!batchLoading && !batchError && batchPlans.length > 0 && (<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{batchPlans.map((batchPlan) => (<div key={batchPlan.batchPlanId} className={`relative cursor-pointer rounded-lg transition-all ${selectedBatchPlans.includes(batchPlan.batchPlanId) ? 'ring-2 ring-primary' : ''}`} onClick={() => toggleBatchPlanSelection(batchPlan.batchPlanId)}>{selectedBatchPlans.includes(batchPlan.batchPlanId) && (<div className="absolute top-2 right-2 z-10 h-5 w-5 rounded-full bg-primary flex items-center justify-center"><span className="text-xs text-primary-foreground">✓</span></div>)}<BatchPlanCard batchPlan={batchPlan} /></div>))}</div>)}
        </>
      ) : (
        <>
          <CrudToolbar entityName="Prep List" entityNamePlural="Prep Lists" selectedCount={0} onAdd={handleAddPrepList} onBulkUpload={handleBulkUpload} exportData={exportData} exportFilename="prep-lists.csv" canEdit={false} canDelete={false} />
          <FormModal title="Create Prep List" description="Create a new prep list for production" isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSave={handleSavePrepList} saveLabel="Create">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Name" required><FormInput value={formData.name} onChange={(e) => setFormData({
                ...formData,
                name: e.target.value 
              })} placeholder="e.g., Morning Prep - Main Kitchen" /></FormField>
              <FormField label="Venue" required><FormSelect value={formData.venueId} onChange={(e) => setFormData({
                ...formData,
                venueId: e.target.value 
              })} placeholder="Select venue..." options={[{
                value: 'venue-main',
                label: 'Main Kitchen' 
              }, {
                value: 'venue-pastry',
                label: 'Pastry Kitchen' 
              }, {
                value: 'venue-cold',
                label: 'Cold Kitchen' 
              }]} /></FormField>
              <FormField label="Prep Date" required><FormInput type="date" value={formData.prepDate} onChange={(e) => setFormData({
                ...formData,
                prepDate: e.target.value 
              })} /></FormField>
            </div>
            <FormField label="Notes"><FormTextarea value={formData.notes} onChange={(e) => setFormData({
              ...formData,
              notes: e.target.value 
            })} placeholder="Special instructions..." rows={2} /></FormField>
          </FormModal>
          <PrepListDetailModal prepList={selectedPrepList} isOpen={showDetailModal} onClose={handleCloseDetailModal} onActivate={handleActivatePrepList} onComplete={handleCompletePrepList} onStartItem={handleStartItem} onCompleteItem={handleCompleteItem} />
          <div className="grid gap-4 md:grid-cols-4">
            <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Prep Lists</CardTitle><FileText className="h-5 w-5 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{stats.total}</div></CardContent></Card>
            <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle><Clock className="h-5 w-5 text-blue-500" /></CardHeader><CardContent><div className="text-2xl font-bold text-blue-600">{stats.active}</div></CardContent></Card>
            <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle><CheckCircle className="h-5 w-5 text-green-500" /></CardHeader><CardContent><div className="text-2xl font-bold text-green-600">{stats.completed}</div></CardContent></Card>
            <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Overall Progress</CardTitle><ChefHat className="h-5 w-5 text-amber-500" /></CardHeader><CardContent><div className="text-2xl font-bold text-amber-600">{stats.progress}%</div></CardContent></Card>
          </div>
          <div className="flex flex-wrap gap-2">{STATUS_TABS.map((tab) => (<Button key={tab.label} variant={selectedStatus === tab.value ? 'default' : 'outline'} size="sm" onClick={() => setSelectedStatus(tab.value)}>{tab.label}</Button>))}</div>
          {isLoading && (<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => (<div key={`skel-${i.toString()}`} className="h-64 animate-pulse rounded-lg bg-muted" />))}</div>)}
          {!isLoading && error && (<Card><CardContent className="flex flex-col items-center justify-center py-12"><ChefHat className="mb-4 h-12 w-12 text-red-500" /><p className="text-lg font-medium">Failed to load prep lists</p><Button variant="outline" className="mt-4" onClick={() => refetch()}>Try Again</Button></CardContent></Card>)}
          {!isLoading && !error && prepLists.length === 0 && (<Card><CardContent className="flex flex-col items-center justify-center py-12"><ChefHat className="mb-4 h-12 w-12 text-muted-foreground" /><p className="text-lg font-medium">No prep lists found</p></CardContent></Card>)}
          {!isLoading && !error && prepLists.length > 0 && (<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{prepLists.map((prepList) => (<div key={prepList.prepListId} className="cursor-pointer rounded-lg transition-all hover:ring-2 hover:ring-primary/50" onClick={() => handlePrepListClick(prepList)}><PrepListCard prepList={prepList} /></div>))}</div>)}
        </>
      )}
    </div>
  );
}
